import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useScanStock, useStockBatch, useStockFieldContext } from '../api/queries'
import type {
  StockBatchLineInput,
  StockItem,
  StockLocation,
  StockMovementKind
} from '../api/types'
import { CodeScanner } from '../components/CodeScanner'
import { normalizeScanned } from '../lib/scanner'
import { Card, EmptyState, ErrorBox, Loading } from '../components/ui'

/**
 * Markens side: scan flere ting i træk og registrér dem på én gang
 * (docs/DECISIONS.md beslutning 071).
 *
 * <b>Ét valg først, så scannes der bare.</b> Montøren står med hænderne fulde ved
 * bagklappen. Skulle han svare på "hvorfra" og "hvortil" for hver eneste vare,
 * ville han holde op med at scanne - og så var hele lageret værdiløst
 * (beslutning 070). Derfor sættes turen op én gang, og resten er kamera.
 *
 * <b>Kurven sendes samlet.</b> Serveren gemmer hele bundtet i én handling, så et
 * afbrudt signal ikke kan efterlade halvdelen bogført.
 */

/** En linje i kurven - varen med, så den kan vises uden et opslag mere. */
interface BasketLine extends StockBatchLineInput {
  item: StockItem
}

/** Turene, som de hedder ude i marken. Koden bagved er systemets. */
const PURPOSES: {
  kind: StockMovementKind
  label: string
  icon: string
  hint: string
  needsFrom: boolean
  needsTo: boolean
  needsProject: boolean
}[] = [
  {
    kind: 'move',
    label: 'Ind i bilen',
    icon: '🚐',
    hint: 'Fra lageret ud i bilen',
    needsFrom: true,
    needsTo: true,
    needsProject: false
  },
  {
    kind: 'consumption',
    label: 'Brugt på sag',
    icon: '🔧',
    hint: 'Materialer der gik til en sag',
    needsFrom: true,
    needsTo: false,
    needsProject: true
  },
  {
    kind: 'receipt',
    label: 'Købt hjem',
    icon: '📦',
    hint: 'Nyt fra grossisten',
    needsFrom: false,
    needsTo: true,
    needsProject: false
  },
  {
    kind: 'removal',
    label: 'Taget ud',
    icon: '🗑️',
    hint: 'Kasseret eller returneret',
    needsFrom: true,
    needsTo: false,
    needsProject: false
  }
]

export function StockFieldPage() {
  const { data: context, isLoading, error } = useStockFieldContext()

  const scan = useScanStock()
  const batch = useStockBatch()

  const [kind, setKind] = useState<StockMovementKind>('consumption')
  const [fromId, setFromId] = useState<number | null>(null)
  const [toId, setToId] = useState<number | null>(null)
  const [projectId, setProjectId] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const [lines, setLines] = useState<BasketLine[]>([])
  const [message, setMessage] = useState<string | null>(null)
  const [receipt, setReceipt] = useState<string | null>(null)

  const purpose = PURPOSES.find((p) => p.kind === kind)!

  const warehouses = useMemo(
    () => context?.locations.filter((l) => l.kind === 'warehouse') ?? [],
    [context]
  )

  // Min bil vaelges selv, naar den findes. Har man ingen - kontoret har ikke -
  // staar feltet tomt, og der skal vaelges frem for at gaettes.
  useEffect(() => {
    if (!context) return

    const mine = context.myLocation?.id ?? null

    if (kind === 'move') {
      setFromId((current) => current ?? warehouses[0]?.id ?? null)
      setToId((current) => current ?? mine)
      return
    }

    if (kind === 'receipt') {
      setToId((current) => current ?? mine)
      setFromId(null)
      return
    }

    // Brugt paa sag og taget ud: begge traekker fra det sted, man staar ved.
    setFromId((current) => current ?? mine)
    setToId(null)
  }, [context, kind, warehouses])

  function choosePurpose(next: StockMovementKind) {
    setKind(next)
    setMessage(null)
    setReceipt(null)

    // Sagen giver kun mening ved forbrug. Blev den staaende, ville et "koebt
    // hjem" bagefter blive bogfoert paa en sag, ingen havde valgt.
    if (next !== 'consumption') {
      setProjectId(null)
    }
  }

  async function handleCode(code: string) {
    setMessage(null)
    setReceipt(null)

    try {
      const result = await scan.mutateAsync(normalizeScanned(code))

      if (result.kind !== 'item' || !result.item) {
        setMessage(result.message ?? `"${result.scannedCode}" blev ikke fundet.`)
        return
      }

      add(result.item)
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Opslaget kunne ikke laves.')
    }
  }

  /**
   * Lægger varen i kurven - eller tæller den op, hvis den er der.
   *
   * At scanne æsken to gange ER måden at sige "to stykker" på, når man står med
   * hænderne fulde. Serveren lægger dem sammen igen, hvis de alligevel skulle
   * nå frem som to linjer.
   */
  function add(item: StockItem) {
    setLines((current) => {
      const index = current.findIndex((l) => l.itemId === item.id)

      if (index < 0) {
        return [...current, { itemId: item.id, quantity: 1, item }]
      }

      const next = [...current]
      next[index] = { ...next[index], quantity: next[index].quantity + 1 }
      return next
    })
  }

  function setQuantity(itemId: number, quantity: number) {
    setLines((current) =>
      current.map((l) => (l.itemId === itemId ? { ...l, quantity } : l))
    )
  }

  function remove(itemId: number) {
    setLines((current) => current.filter((l) => l.itemId !== itemId))
  }

  /** Hvad der mangler, før kurven kan sendes. Null = klar. */
  const blocker = useMemo(() => {
    if (lines.length === 0) return 'Der er ikke scannet noget endnu.'
    if (lines.some((l) => !(l.quantity > 0))) return 'Alle antal skal være større end nul.'
    if (purpose.needsFrom && !fromId) return 'Vælg hvor varerne kommer fra.'
    if (purpose.needsTo && !toId) return 'Vælg hvor varerne skal hen.'
    if (purpose.needsProject && !projectId) return 'Vælg sagen materialerne gik til.'
    return null
  }, [lines, purpose, fromId, toId, projectId])

  async function submit() {
    if (blocker) {
      setMessage(blocker)
      return
    }

    setMessage(null)

    try {
      const result = await batch.mutateAsync({
        kind,
        fromLocationId: purpose.needsFrom ? fromId : null,
        toLocationId: purpose.needsTo ? toId : null,
        projectId: purpose.needsProject ? projectId : null,
        note: note.trim() || null,
        lines: lines.map((l) => ({ itemId: l.itemId, quantity: l.quantity }))
      })

      // Kurven toemmes foerst NAAR serveren har svaret. Toemte vi den med det
      // samme, ville en fejlet forbindelse koste hele turen.
      setLines([])
      setNote('')
      setReceipt(
        result.itemCount === 1
          ? '1 vare er registreret.'
          : `${result.itemCount} varer er registreret.`
      )
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Registreringen kunne ikke gemmes.')
    }
  }

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!context) return null

  const projects = context.projects

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row row--between" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title" style={{ margin: 0 }}>
          Scan ind og ud
        </h1>
        <Link className="btn btn--sm" to="/stock/locations">
          Steder og beholdning
        </Link>
      </div>

      <Card title="Hvad skal der ske?">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {PURPOSES.map((p) => (
            <button
              key={p.kind}
              className={`btn ${p.kind === kind ? 'btn--primary' : ''}`}
              onClick={() => choosePurpose(p.kind)}
              style={{ flex: '1 1 150px', minHeight: 52 }}
            >
              <span aria-hidden="true" style={{ marginRight: 6 }}>
                {p.icon}
              </span>
              {p.label}
            </button>
          ))}
        </div>

        <div className="small muted" style={{ marginTop: 8 }}>
          {purpose.hint}
        </div>

        <div className="form-grid" style={{ marginTop: 14 }}>
          {purpose.needsFrom && (
            <LocationPicker
              label="Fra"
              locations={context.locations}
              value={fromId}
              onChange={setFromId}
              mine={context.myLocation}
            />
          )}

          {purpose.needsTo && (
            <LocationPicker
              label="Til"
              locations={context.locations}
              value={toId}
              onChange={setToId}
              mine={context.myLocation}
            />
          )}

          {purpose.needsProject && (
            <div>
              <div className="tiny muted">Sag</div>
              <select
                value={projectId ?? ''}
                onChange={(event) =>
                  setProjectId(event.target.value ? Number(event.target.value) : null)
                }
              >
                <option value="">Vælg sag ...</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.customerName ? `${p.name} - ${p.customerName}` : p.name}
                  </option>
                ))}
              </select>
              {projects.length === 0 && (
                <div className="tiny muted" style={{ marginTop: 4 }}>
                  Du står ikke på nogen åbne sager.
                </div>
              )}
            </div>
          )}
        </div>

        {!context.myLocation && (
          <div className="map-notice" style={{ marginTop: 12 }}>
            Du står ikke som ansvarlig for en bil, så der er ikke valgt et sted for dig.
            Vælg det selv ovenfor - eller få en administrator til at sætte dig på bilen
            under Steder.
          </div>
        )}
      </Card>

      <Card title="Scan varerne">
        <CodeScanner
          onCode={(code) => void handleCode(code)}
          busy={scan.isPending}
          hint="Scan videre - hver vare lægges i kurven"
        />

        {scan.isPending && (
          <div className="small muted" style={{ marginTop: 10 }}>
            Slår op ...
          </div>
        )}

        {message && (
          <div className="map-notice" style={{ marginTop: 12 }}>
            {message}
          </div>
        )}

        {receipt && (
          <div className="map-notice" style={{ marginTop: 12 }}>
            {receipt}
          </div>
        )}
      </Card>

      <Card title={lines.length === 0 ? 'Kurven' : `Kurven (${lines.length})`}>
        {lines.length === 0 ? (
          <EmptyState>
            Ingenting scannet endnu. Hold telefonen op mod et mærkat eller en stregkode - den
            samme vare scannet to gange tæller to.
          </EmptyState>
        ) : (
          <div className="stack" style={{ gap: 10 }}>
            {lines.map((line) => (
              <div
                key={line.itemId}
                className="row row--between"
                style={{ alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
              >
                <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                  <div className="small" style={{ fontWeight: 600 }}>
                    {line.item.name}
                  </div>
                  <div className="tiny muted">
                    {[line.item.supplierNumber, line.item.code].filter(Boolean).join(' · ')}
                  </div>
                </div>

                <div className="row" style={{ gap: 6, alignItems: 'center' }}>
                  <button
                    className="btn btn--sm"
                    aria-label={`Færre ${line.item.name}`}
                    onClick={() => setQuantity(line.itemId, Math.max(0, line.quantity - 1))}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="any"
                    min="0"
                    value={line.quantity}
                    onChange={(event) => setQuantity(line.itemId, Number(event.target.value))}
                    style={{ width: 84, textAlign: 'right' }}
                  />
                  <span className="tiny muted" style={{ minWidth: 40 }}>
                    {line.item.unit}
                  </span>
                  <button
                    className="btn btn--sm"
                    aria-label={`Flere ${line.item.name}`}
                    onClick={() => setQuantity(line.itemId, line.quantity + 1)}
                  >
                    +
                  </button>
                  <button
                    className="btn btn--sm btn--danger"
                    aria-label={`Fjern ${line.item.name}`}
                    onClick={() => remove(line.itemId)}
                  >
                    Fjern
                  </button>
                </div>
              </div>
            ))}

            <div>
              <div className="tiny muted">Note (valgfri)</div>
              <input
                placeholder="Hvor i bygningen, hvilket rum ..."
                value={note}
                onChange={(event) => setNote(event.target.value)}
                maxLength={500}
              />
              <div className="tiny muted" style={{ marginTop: 4 }}>
                Noten står på hver linje i bundtet - det er den samme tur.
              </div>
            </div>

            <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn btn--primary"
                disabled={batch.isPending || blocker !== null}
                onClick={() => void submit()}
                style={{ minHeight: 48, flex: '1 1 200px' }}
              >
                {batch.isPending
                  ? 'Gemmer ...'
                  : `Registrér ${lines.length === 1 ? '1 vare' : `${lines.length} varer`}`}
              </button>
              <button className="btn" disabled={batch.isPending} onClick={() => setLines([])}>
                Tøm kurven
              </button>
            </div>

            {blocker && <div className="tiny muted">{blocker}</div>}
          </div>
        )}
      </Card>
    </div>
  )
}

/**
 * Vælgeren af sted. Min egen bil står øverst med en markering, fordi det er den,
 * der vælges ni gange ud af ti.
 */
function LocationPicker({
  label,
  locations,
  value,
  onChange,
  mine
}: {
  label: string
  locations: StockLocation[]
  value: number | null
  onChange: (id: number | null) => void
  mine?: StockLocation | null
}) {
  return (
    <div>
      <div className="tiny muted">{label}</div>
      <select
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value ? Number(event.target.value) : null)}
      >
        <option value="">Vælg sted ...</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>
            {l.id === mine?.id ? `${l.name} (min bil)` : `${l.name} - ${l.kindName.toLowerCase()}`}
          </option>
        ))}
      </select>
    </div>
  )
}
